import { gql, useMutation, useQuery } from '@apollo/client';
import BadWordsFilter from 'bad-words';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import BodyText from '@ui/atoms/BodyText';
import FlexColumn from '@ui/atoms/FlexColumn';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';
import Button from '@ui/atoms/Ws-Button';

import TextInput from '@ui/forms/TextInput';

import theme from '@theme/WsTheme';

const PageWrapper = styled.div`
	flex-flow: column;
	background: ${theme.colors.lightBeige};
	position: relative;
	max-width: 120rem;
	margin-left: auto;
	margin-right: auto;
`;

const Headline = styled.div`
	font-size: ${theme.fontSizes.header.lg};
	padding: 2rem;
`;

const HeaderContainer = styled.div`
	width: 100%;
	text-align: center;
	padding: 3rem;
`;

const FooterContainer = styled.div`
	width: 100%;
	padding-top: 3rem;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.lg};

	a {
		margin: 0 3rem 0 3rem;
		display: inline-flex;
		color: ${theme.colors.black};
		text-decoration: underline;
		flex-direction: column;
		align-items: center;
		text-align: center;
		@media ${theme.responsive.breakpoints.tablet} {
			flex-direction: row;
		}
	}
`;

const AgreementWrapper = styled(FlexColumn)`
	align-items: stretch;
	padding: ${theme.spacing.md};
	gap: ${theme.spacing.md};
	${WYSIWYGContent} {
		margin-top: ${theme.spacing.xs};
	}

	@media ${theme.responsive.breakpoints.tablet} {
		padding: ${theme.spacing.xxxl};
	}
`;

const FormWrapper = styled(FlexColumn)`
	gap: ${theme.spacing.md};
`;

const NICKNAME_QUERY = gql`
	query NicknameQuery {
		currentMember {
			id
			username
			nickname
		}
	}
`;

const NICKNAME_MUTATION = gql`
	mutation UpdateNickname($clientMutationId: String, $memberId: UUID!, $patch: MemberPatch!) {
		updateMember(input: { clientMutationId: $clientMutationId, id: $memberId, patch: $patch }) {
			clientMutationId
			member {
				id
				nickname
			}
		}
	}
`;

interface INicknameMutationResponse {
	updateMember: {
		clientMutationId: string;
		member: {
			id: string;
			username: string;
			nickname: string;
		};
	};
}

type NicknameFormFields = {
	nickname: string;
};

const InnerspaceNickname: FC = () => {
	const badWordsFilter: BadWordsFilter = new BadWordsFilter();

	// Form
	const {
		register: signupFormFieldRegister,
		handleSubmit: handleSignupFormSubmit,
		setError: setSignupFormError,
		formState: { errors: nicknameFormErrors },
	} = useForm<NicknameFormFields>({ mode: 'onSubmit' });

	// GraphQL
	const {
		loading,
		error,
		data: accountData,
	} = useQuery<{
		currentMember: {
			id: string;
			username: string;
			nickname: string;
		};
	}>(NICKNAME_QUERY);

	const member = accountData?.currentMember;

	const [updateNickname, { loading: updateNicknameLoading, error: updateNicknameError }] =
		useMutation<INicknameMutationResponse>(NICKNAME_MUTATION);

	// Butter
	const {
		butterPage: { nickname_header, nickname_explanation, nickname_requirements },
	} = useStaticQuery(
		graphql`
			{
				butterPage(slug: { eq: "nickname" }) {
					nickname_header
					nickname_explanation
					nickname_requirements
				}
			}
		`
	);

	// Form Handler

	const onFormSubmit = async (data: NicknameFormFields) => {
		try {
			await updateNickname({
				variables: {
					clientMutationId: member?.id,
					memberId: member?.id,
					patch: {
						nickname: data.nickname.trim(),
					},
				},
			});
			navigate(process.env.GATSBY_COMMUNITY_SSO_LINK ?? '');
		} catch (e) {
			console.error('caught error when submitting acceptance: ' + e);
		}
	};

	if (loading) {
		return <div>loading...</div>;
	}

	if (member?.nickname !== null) {
		navigate(process.env.GATSBY_COMMUNITY_SSO_LINK ?? '');
		return (
			<PageWrapper>
				<HeaderContainer>
					<Headline>Redirecting...</Headline>
				</HeaderContainer>
			</PageWrapper>
		);
	} else {
		return (
			<PageWrapper>
				<HeaderContainer>
					<Headline>{nickname_header}</Headline>
				</HeaderContainer>
				<AgreementWrapper>
					<form onSubmit={handleSignupFormSubmit(onFormSubmit)}>
						<FormWrapper>
							<TextInput
								label="Display name"
								{...signupFormFieldRegister('nickname', {
									required: true,
									minLength: { value: 1, message: 'Enter at least one character' },
									maxLength: { value: 30, message: 'Enter 30 characeters or less' },
									pattern: /^[A-Za-z0-9\s]+$/i,
									validate: {
										badWords: (value) => !badWordsFilter.isProfane(value.replace(/[\W\d]/g, '')),
									},
								})}
								fullWidth
								error={Boolean(nicknameFormErrors.nickname)}
								errorLabel={nicknameFormErrors.nickname?.message}
							/>
							<BodyText>{nickname_explanation}</BodyText>
							<WYSIWYGContent
								bulletType="disc"
								dangerouslySetInnerHTML={{ __html: nickname_requirements }}
							/>
							<Button type="submit" label="Create my account" disabled={updateNicknameLoading} />
						</FormWrapper>
					</form>
				</AgreementWrapper>
				<FooterContainer>
					<Footer>{updateNicknameError}</Footer>
				</FooterContainer>
			</PageWrapper>
		);
	}
};
export default InnerspaceNickname;
